'use client';

import React, { useState } from 'react';
import { Box, Typography, Button, TextField, MenuItem, Alert } from '@mui/material';
import { TabAwareModal } from '@/shared/ui/overlay/Modal';
import { plannerApi, type PlannerView, type CreatePlannerRequest } from '@/entities/planner';

/** RegisterPlannerRequest.transportationType (OpenAPI enum) */
const TRANSPORT_OPTIONS = [
  { value: 'CAR', label: '자차' },
  { value: 'PUBLIC_TRANSPORT', label: '대중교통' },
];

/** RegisterPlannerRequest.employmentType (OpenAPI enum) */
const EMPLOYMENT_OPTIONS = [
  { value: 'CONTRACT', label: '계약직' },
  { value: 'FREELANCER', label: '프리랜서' },
];

interface AddPlannerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (planner: PlannerView) => void;
}

export function AddPlannerModal({ open, onClose, onSuccess }: AddPlannerModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [transportationType, setTransportationType] = useState('CAR');
  const [employmentType, setEmploymentType] = useState('CONTRACT');
  const [hubspotId, setHubspotId] = useState('');
  const [misoPartnerId, setMisoPartnerId] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setPhone('');
    setTransportationType('CAR');
    setEmploymentType('CONTRACT');
    setHubspotId('');
    setMisoPartnerId('');
    setHomeAddress('');
    setError(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const canSubmit =
    name.trim().length > 0 &&
    phone.trim().length > 0 &&
    transportationType &&
    employmentType &&
    hubspotId.trim().length > 0 &&
    misoPartnerId.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const request: CreatePlannerRequest = {
        name: name.trim(),
        phone: phone.trim(),
        transportationType,
        employmentType,
        hubspotId: hubspotId.trim(),
        misoPartnerId: misoPartnerId.trim(),
      };
      if (homeAddress.trim()) request.homeAddress = homeAddress.trim();

      const created = await plannerApi.createPlanner(request);
      resetForm();
      onClose();
      onSuccess?.(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : '플래너 추가에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TabAwareModal
      open={open}
      onClose={handleClose}
      title="플래너 추가"
      maxWidth="sm"
      fullWidth
      contentProps={{ sx: { mt: 1 } }}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button variant="contained" disabled={!canSubmit || isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? '추가 중...' : '추가'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            이름 *
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="플래너 이름"
          />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            연락처 *
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="010-0000-0000"
          />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            이동수단 *
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={transportationType}
            onChange={(e) => setTransportationType(e.target.value)}
          >
            {TRANSPORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            고용형태 *
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value)}
          >
            {EMPLOYMENT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            HubSpot ID *
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={hubspotId}
            onChange={(e) => setHubspotId(e.target.value)}
            placeholder="필수"
          />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Miso Partner ID *
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={misoPartnerId}
            onChange={(e) => setMisoPartnerId(e.target.value)}
            placeholder="필수"
          />
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            자택 주소
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            value={homeAddress}
            onChange={(e) => setHomeAddress(e.target.value)}
            placeholder="(선택)"
          />
        </Box>
      </Box>
    </TabAwareModal>
  );
}
